import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { categoryIcon } from 'src/app/app-icons';
import { EventTypeModel } from 'src/app/models/events/event-type.model';
import { LearningRecommendationModel } from 'src/app/models/learning-recommendations/learning-recommendation.model';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { DashboardWidgetsDataService } from 'src/app/services/dashboard-widget-data.service';
import { getTrainingTypeLabel } from 'src/app/utils/training-type.utils';

@Component({
  selector: 'app-learning-recommendations-widget',
  standalone: false,
  templateUrl: './learning-recommendations-widget.component.html',
  styleUrl: './learning-recommendations-widget.component.scss',
})
export class LearningRecommendationsWidgetComponent implements OnInit {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  
  recommendations: LearningRecommendationModel[] = [];

  canScrollLeft = false;
  canScrollRight = true;
  scrollAmount = 476;

  @Input() isLoaded: boolean = false;

  @Output() onReload = new EventEmitter<void>();

  constructor(
    private dashboardWidgetsDataService: DashboardWidgetsDataService,
    private translateService: TranslateService,
    private sanitized: DomSanitizer,
  ) {}

  ngOnInit() {
    this.dashboardWidgetsDataService
      .getLearningRecommendations()
      .subscribe(data => {
        if (data) {
          this.recommendations = data;
        }
      });
  }

  ngAfterViewInit() {
    this.checkScrollLimits();

    const firstCard = this.scrollContainer.nativeElement.querySelector('.recommendation-card') as HTMLElement;
    if (firstCard) {
      this.scrollAmount = firstCard.offsetWidth + 16; // card width + gap
    }
  }

  handleReload() {
    this.onReload.emit();
    this.resetCanScroll();
  }

  resetCanScroll() {
    this.canScrollLeft = false;
    this.canScrollRight = true;
  }

  checkScrollLimits() {
    const el = this.scrollContainer.nativeElement;
    this.canScrollLeft = el.scrollLeft > 2;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
  }

  scroll(direction: 'left' | 'right') {
    const el = this.scrollContainer.nativeElement;
    
    el.scrollBy({
      left: direction === 'right' ? this.scrollAmount : -this.scrollAmount,
      behavior: 'smooth'
    });

    setTimeout(() => this.checkScrollLimits(), 300);
  }
  
  isTraining(recommendation: LearningRecommendationModel) {
    return recommendation.eventType === EventTypeModel.Normal;
  }

  getTrainingTypeLabel(trainingType: TrainingTypeModel) {
    return getTrainingTypeLabel(trainingType, this.translateService)
  }

  getCategoryIcon() {
    return this.sanitized.bypassSecurityTrustHtml(categoryIcon);
  }

  getTrainingCategoriesLabel(recommendation: LearningRecommendationModel): string | null {
    const categories = recommendation?.categories;
    if (!categories?.length) return null;

    const first = categories[0].name;
    const extraCount = categories.length - 1;

    return extraCount > 0 ? `${first} (+${extraCount})` : first;
  }

  isComplianceTrainingType(recommendation: LearningRecommendationModel): boolean {
    return recommendation.trainingType == TrainingTypeModel.Compliance;
  }

  getRecommendationLink(recommendation: LearningRecommendationModel): any[] {
    if (this.isTraining(recommendation)) {
      return ['/my-trainings', 'training', recommendation.id];
    } else {
      return ['/my-trainings', 'course', recommendation.id];
    }
  }
}
